/**
 * Marks an element as a drop target. Returns the ref to register plus `isOver`
 * (this droppable is the current target) and `activeId` (the dragged id, or
 * `null`) so the consumer can style the bucket and react to the active drag.
 */
import { useRef } from 'react'
import { useDndContext } from './DndContext.tsx'
import type { DndData, Orientation } from './types.ts'

export interface UseDroppableArgs {
  id: string
  data?: DndData
  disabled?: boolean
  /** Hint for keyboard sortable traversal; buckets can leave it default. */
  orientation?: Orientation
}

export interface UseDroppableResult {
  setNodeRef: (node: HTMLElement | null) => void
  isOver: boolean
  activeId: string | null
}

export function useDroppable({
  id,
  data,
  disabled = false,
  orientation = 'vertical',
}: UseDroppableArgs): UseDroppableResult {
  const ctx = useDndContext()

  const dataRef = useRef(data)
  dataRef.current = data
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled

  const entryRef = useRef({
    id,
    node: null as HTMLElement | null,
    getData: () => dataRef.current ?? {},
    isDisabled: () => disabledRef.current,
    orientation,
  })
  entryRef.current.id = id
  entryRef.current.orientation = orientation

  // Register in the ref callback (commit) rather than an effect — same reasoning
  // as `useDraggable`: the registry is populated synchronously when the node
  // mounts, with no passive-effect timing in the loop. Stable callback +
  // idempotent guard (StrictMode-safe).
  const { registerDroppable } = ctx
  const unregisterRef = useRef<(() => void) | null>(null)
  const setNodeRef = useRef((node: HTMLElement | null) => {
    entryRef.current.node = node
    if (node) {
      if (!unregisterRef.current) unregisterRef.current = registerDroppable(entryRef.current)
    } else {
      unregisterRef.current?.()
      unregisterRef.current = null
    }
  }).current

  return {
    setNodeRef,
    isOver: ctx.overId === id,
    activeId: ctx.activeId,
  }
}
